import React, { useState, useEffect } from "react";
import PagesHeader from "@components/PagesHeader";
import SectionHeader from "@components/SectionHeader";
import BrandLogo from "../../components/BrandLogo";
import StickyButton from "@components/StickyButton";
import Button from "@components/Button";
import NiceModal from "@ebay/nice-modal-react";
import UnderlineButton from "@components/UnderlineButton";
import { getCouponCatalogueDescribeUltraInsider } from "../../api/coupon";
import { PRIZE_CACHE_TIME, PRIZE_STALE_TIME } from "../../api/prize";
import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate, useLocation } from "react-router";
import { getUltraInsiderPrizeList } from "../../api/prize";
import {
  getModuleValue,
  parseCMSBold,
  parseHTMLToText,
  useWindowSize,
  isCouponInStock,
  getCouponAvailability,
  shouldRedirectCouponToActivation,
  getActivationActivityPath,
  getCouponRaffleOutroType,
  getCouponRaffleOutroContent,
  getCouponRaffleOutroSectionTitle,
  isCouponPlaceholder,
  findGameRewardPrizeInList,
  getGameRewardPrizeRedeemPath,
} from "../../Helpers";
import "./style.scss";
import {
  DETAILS_CACHE_TIME,
  DETAILS_REFETCH_INVERVAL,
  DETAILS_STALE_TIME,
  getUserDetails,
} from "../../api/consumer";
import {
  COUPON_CACHE_TIME,
  COUPON_STALE_TIME,
  getCouponList,
} from "../../api/coupon";
import { fetchData } from "../../Helpers";
import DOMPurify from "dompurify";
import WarningIcon from "@assets/warning.svg?react";

function getRewardUuidByIdent(rewards, ident) {
  if (!Array.isArray(rewards)) return null;

  const found = rewards.find((item) => item.catalogue.ident === ident);
  return found ? found.uuid : null;
}

function getRewardByUuid(rewards, uuid) {
  if (!Array.isArray(rewards) || !uuid) return null;

  return rewards.find((item) => item.uuid === uuid) ?? null;
}

export default function RedeemAwardPage() {
  const { width } = useWindowSize();
  const { ident } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(0);

  function formatDate(timestamp) {
    const date = new Date(timestamp);
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  }

  const { data, refetch: refetchData } = useQuery({
    queryKey: ["coupon-catalogue-ultrainsider-describe", ident],
    queryFn: () => getCouponCatalogueDescribeUltraInsider(ident),
    staleTime: PRIZE_STALE_TIME,
    cacheTime: PRIZE_CACHE_TIME,
    refetchOnMount: "always",
  });

  const prizeCatalogue = useQuery({
    queryKey: ["prize-ultra-insider", "include-gamereward"],
    queryFn: () =>
      getUltraInsiderPrizeList({
        includeGameRewards: true,
        device: ["web"],
        target: ["web"],
      }),
    enabled: isCouponPlaceholder(ident) || !!ident,
    refetchOnMount: "always",
  });

  const prizeIdent = getRewardUuidByIdent(
    prizeCatalogue.data?.prizes ?? [],
    ident ?? "",
  );

  const matchedPrize = getRewardByUuid(
    prizeCatalogue.data?.prizes ?? [],
    prizeIdent,
  );

  const prizeCatalogueData = matchedPrize?.catalogue;

  const detailsQuery = useQuery({
    queryKey: ["user-details"],
    queryFn: () => getUserDetails(),
    refetchInterval: DETAILS_REFETCH_INVERVAL,
    staleTime: DETAILS_STALE_TIME,
    cacheTime: DETAILS_CACHE_TIME,
  });

  const prizeListQuery = useQuery({
    queryKey: ["coupon-list"],
    queryFn: () => getCouponList(),
    staleTime: COUPON_STALE_TIME,
    cacheTime: COUPON_CACHE_TIME,
  });

  const partnerAvailable = prizeCatalogue.data?.partnerAvailable;
  const partnerAvailableCoupon = prizeCatalogue.data?.catalogue?.find(
    (coupon) => coupon.ident === ident,
  )?.partner;

  const partnerRedeemExists = data?.content?.modules?.some(
    (module) => module.type === "partner_redeem",
  );

  const peroidConfig = {
    DAY: "Tag",
    WEEK: "Woche",
    MONTH: "Monat",
  };

  const logosModules = getModuleValue(
    data?.content?.modules,
    partnerRedeemExists ? "partner_redeem" : "online_redeem",
    "metadata.logos",
  );

  const isMonoColor = getModuleValue(
    data?.content?.modules,
    partnerRedeemExists ? "partner_redeem" : "online_redeem",
    "metadata.monoColor",
  );

  useEffect(() => {
    if (!partnerAvailable && partnerAvailableCoupon) {
      NiceModal.show("partner-available-modal");
    }
  }, [partnerAvailable, partnerAvailableCoupon]);

  const gameRewardPrize =
    isCouponPlaceholder(ident) && prizeCatalogue.isSuccess
      ? findGameRewardPrizeInList(prizeCatalogue.data?.prizes, ident)
      : null;
  const hasGameReward = !!gameRewardPrize;

  useEffect(() => {
    if (
      prizeIdent ||
      !isCouponPlaceholder(ident) ||
      !prizeCatalogue.isSuccess
    ) {
      return;
    }

    const gameReward = findGameRewardPrizeInList(
      prizeCatalogue.data?.prizes,
      ident,
    );
    if (!gameReward) return;

    const path = getGameRewardPrizeRedeemPath(gameReward, {
      ultraInsider: true,
    });
    if (path && location.pathname !== path) {
      navigate(path, { replace: true });
    }
  }, [
    prizeIdent,
    ident,
    prizeCatalogue.isSuccess,
    prizeCatalogue.data?.prizes,
    navigate,
    location.pathname,
  ]);

  const redirectToActivation =
    !prizeIdent && !hasGameReward && shouldRedirectCouponToActivation(data);
  const activationPath = getActivationActivityPath(data?.activation);
  const raffleOutroType =
    !prizeIdent && !hasGameReward && getCouponRaffleOutroType(data);
  const showRaffleOutro = !!raffleOutroType;
  const availability = getCouponAvailability(data?.availabilityLevel);
  const redeemModuleType = partnerRedeemExists
    ? "partner_redeem"
    : "online_redeem";

  function redeemButtonCopy() {
    if (prizeIdent && prizeCatalogueData) {
      if (!prizeCatalogueData.inStock) {
        return "Nicht lieferbar";
      } else if (!prizeCatalogueData.hasLevel) {
        return "Status unpassend";
      } else if (!prizeCatalogueData.hasPoints) {
        return "Mehr Punkte sammeln";
      } else if (
        data?.limitExceeded &&
        data?.ident?.includes("wunschgutschein")
      ) {
        return "Wöchentliche Einlöselimit erreicht ";
      } else if (data?.limitExceeded) {
        return "Einlöselimit erreicht";
      } else {
        return "Gutschein abholen";
      }
    }

    if (data) {
      if (redirectToActivation) {
        return "Jetzt teilnehmen";
      }
      if (!partnerAvailable && partnerAvailableCoupon) {
        return "Gutschein gesperrt";
      } else if (!isCouponInStock(data)) {
        return "Nicht lieferbar";
      } else if (!data.hasLevel) {
        return "Status unpassend";
      } else if (!data.hasPoints) {
        return "Mehr Punkte sammeln";
      } else if (data.periodicLimitExceeded) {
        return "Einlöselimit erreicht";
      } else if (
        data.periodicLimitExceeded &&
        data?.ident?.includes("wunschgutschein")
      ) {
        return "Wöchentliche Einlöselimit erreicht ";
      } else {
        return "Gutschein abholen";
      }
    }
  }

  function redeemButtonLock() {
    if (prizeIdent) {
      if (!matchedPrize) {
        return true;
      }

      if (prizeCatalogueData?.inStock === false) {
        return true;
      }
      if (prizeCatalogueData?.hasLevel === false) {
        return true;
      }
      if (prizeCatalogueData?.hasPoints === false) {
        return true;
      }
      if (data?.limitExceeded) {
        return true;
      }

      return false;
    }

    return !data?.available;
  }

  const endpoint = prizeIdent ? "/prize/insider/redeem" : "/coupon/redeem";

  const payload = prizeIdent
    ? {
        prize: {
          uuid: DOMPurify.sanitize(prizeIdent),
        },
      }
    : {
        catalogue: {
          ident: DOMPurify.sanitize(ident),
        },
      };

  function redeemAward() {
    fetchData({
      endpoint,
      payload,

      successCallback: (resObj) => {
        setLoading(0);
        refetchData();
        detailsQuery.refetch();
        prizeCatalogue.refetch();
        prizeListQuery.refetch();

        NiceModal.show("redeem-award-modal", {
          awardName: parseCMSBold(data?.content?.description ?? ""),
          ident,
          bg: getModuleValue(data?.content?.modules, "thumbnail", "file.url"),
          imgDesc: getModuleValue(
            data?.content?.modules,
            "thumbnail",
            "metadata.imgDesc",
          ),
          awardUUID: resObj.data[0].uuid,
          brands: data?.brands,
          logos: logosModules,
          isUltra: true,
          isMonoColor,
          title: data?.content?.title,
          customModal: data?.content?.modules?.find(
            (module) => module.type === "custom_redem_award_modal",
          ),
        });
      },

      errorCallback: () => {
        setLoading(0);
        detailsQuery.refetch();
      },

      setLoading,
    });
  }

  return (
    <section
      className={`redeem-award-page ultra-insider${showRaffleOutro ? " raffle-pending" : ""}`}
    >
      <PagesHeader
        back
        dangerouslySetInnerHTML={parseCMSBold(data?.content?.title ?? "")}
      />
      <div className="desktop-wrapper">
        {width >= 1020 && (
          <div className="award-img-desktop">
            <img
              src={getModuleValue(
                data?.content?.modules,
                "hero_image",
                "file.url",
              )}
              alt={getModuleValue(
                data?.content?.modules,
                "hero_image",
                "metadata.imgDesc",
              )}
            />
            <BrandLogo brands={data?.brands} />
            {getModuleValue(data?.content?.modules, "text", "custom") && (
              <div className="warning-box">
                <div className="warning-icon">
                  <WarningIcon />
                </div>
                <div
                  className="warning-content"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(getModuleValue(
                      data?.content?.modules,
                      "text",
                      "custom",
                    )),
                  }}
                ></div>
              </div>
            )}
          </div>
        )}
        <div className="main-content">
          {width < 1020 && (
            <>
              <div className="award-img-mobile">
                <img
                  src={getModuleValue(
                    data?.content?.modules,
                    "thumbnail",
                    "file.url",
                  )}
                  alt={getModuleValue(
                    data?.content?.modules,
                    "thumbnail",
                    "metadata.imgDesc",
                  )}
                />
                <BrandLogo brands={data?.brands} />
              </div>
              {getModuleValue(data?.content?.modules, "text", "custom") && (
                <div className="warning-box">
                  <div className="warning-icon">
                    <WarningIcon />
                  </div>
                  <div
                    className="warning-content"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(getModuleValue(
                        data?.content?.modules,
                        "text",
                        "custom",
                      )),
                    }}
                  ></div>
                </div>
              )}
            </>
          )}
          <div className="award-summary">
            <SectionHeader>
              {showRaffleOutro ? (
                <>{getCouponRaffleOutroSectionTitle(raffleOutroType)}</>
              ) : (
                <>
                  <strong>Einzelheiten</strong> des Angebots
                </>
              )}
            </SectionHeader>
            <div
              className="content"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(showRaffleOutro
                  ? getCouponRaffleOutroContent(data)
                  : getModuleValue(
                      data?.content?.modules,
                      redeemModuleType,
                      "metadata.about",
                    )),
              }}
            />
            {!showRaffleOutro && (
              <>
                {availability && (
                  <p className="availability">
                    <span>Verfügbarkeit:</span>{" "}
                    <span className="highlight">{availability.label}</span>
                    <span className="availability-bar" aria-hidden="true">
                      <span
                        style={{ width: `${availability.progress * 100}%` }}
                      />
                    </span>
                  </p>
                )}
                <p>
                  <span>KOSTET:</span>{" "}
                  <span className="highlight">{data?.price} PUNKTE</span>
                </p>
                <p>
                  <span>Ablaufdatum:</span>{" "}
                  <span className="highlight">
                    {formatDate(data.expirationDate)}
                  </span>
                </p>
                {data.period !== null && (
                  <p>
                    <span>Einlöselimit:</span>{" "}
                    <span className="highlight">
                      1 pro {peroidConfig[data.period]}
                    </span>
                  </p>
                )}
              </>
            )}
            {width >= 1020 && !showRaffleOutro && (
              <Button
                href={redirectToActivation ? activationPath : undefined}
                action={redirectToActivation ? undefined : () => redeemAward()}
                disabled={redirectToActivation ? false : redeemButtonLock()}
              >
                {redeemButtonCopy()}
              </Button>
            )}
          </div>

          {!showRaffleOutro && logosModules?.length > 0 && (
            <div className="award-info">
              <SectionHeader>
                <strong>Zur Verwendung auf:</strong>
              </SectionHeader>
              <div className={`logos ${isMonoColor ? "monoColor" : ""}`}>
                {logosModules.map((e, index) => (
                  <img src={e.file.url} alt={e.alt} key={index} />
                ))}
              </div>
            </div>
          )}

          {!showRaffleOutro &&
            parseHTMLToText(
              getModuleValue(
                data?.content?.modules,
                redeemModuleType,
                "metadata.howToUse",
              ),
              "li",
              true,
            )?.length !== 0 && (
              <UnderlineButton
                className="how-to-use"
                onClick={() =>
                  NiceModal.show("howToUse-modal", {
                    howToUse: parseHTMLToText(
                      getModuleValue(
                        data?.content?.modules,
                        partnerRedeemExists
                          ? "partner_redeem"
                          : "online_redeem",
                        "metadata.howToUse",
                      ),
                      "li",
                      true,
                    ),
                  })
                }
              >
                Anwendungstipps
              </UnderlineButton>
            )}
        </div>
      </div>
      {width < 1020 && !showRaffleOutro && (
        <StickyButton
          href={redirectToActivation ? activationPath : undefined}
          action={redirectToActivation ? undefined : () => redeemAward()}
          disabled={redirectToActivation ? false : redeemButtonLock()}
        >
          {redeemButtonCopy()}
        </StickyButton>
      )}
    </section>
  );
}
